'use client';

import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import type { MaterialCardType } from '../hooks/use-project-references-panel';

interface MaterialTypeSelectorProps {
  /** The item's current card type — highlighted and inert. */
  currentType: MaterialCardType;
  /** Types offered as switch targets, in display order. */
  options: MaterialCardType[];
  /** Disable every pill (a change is in flight, or the card is busy). */
  disabled?: boolean;
  /** Show a spinner on the non-current pills while a change is in flight. */
  busy?: boolean;
  onSelect: (target: MaterialCardType) => void;
}

/**
 * Shared "type" selector — ONE row of pills (View · Document · Reference ·
 * Output style) used by both the reference detail dialog and the angle (View)
 * edit dialog, so "switch what this item is used for" reads identically across
 * cards (mirrors Apple's standalone `MaterialTypeSelector`). The current type is
 * highlighted; tapping another runs `onSelect`. The caller maps the target to
 * the right server hop (single PATCH for Document/Reference/Output, a
 * materials/convert — plus a follow-up PATCH — when an angle is involved) and
 * surfaces any verbatim 422; no gating here.
 */
export function MaterialTypeSelector({
  currentType,
  options,
  disabled = false,
  busy = false,
  onSelect,
}: MaterialTypeSelectorProps) {
  const t = useTranslations('projectReference');

  return (
    <div className="space-y-1.5 pt-1">
      <label className="text-form-label">{t('detail.typeLabel')}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((type) => {
          const isCurrent = type === currentType;
          // "Output style" reuses the existing category.output label; the other
          // three live under detail.type.
          const label =
            type === 'output'
              ? t('category.output')
              : t(`detail.type.${type}`);
          return (
            <button
              key={type}
              type="button"
              aria-pressed={isCurrent}
              // Don't disable the current pill — `disabled:opacity-50` would dim
              // it. The onClick guard already makes it inert.
              disabled={disabled}
              onClick={() => {
                if (!isCurrent) onSelect(type);
              }}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-chip whitespace-nowrap transition-colors disabled:opacity-50',
                isCurrent
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/70'
              )}
            >
              {busy && !isCurrent && <Loader2 className="w-3 h-3 animate-spin" />}
              {label}
            </button>
          );
        })}
      </div>
      <p className="text-card-meta text-muted-foreground">
        {t('detail.typeHelper')}
      </p>
    </div>
  );
}
